import { useEffect, useRef, useState } from "react";
import SC from "./style";
import { Button, Loader } from "@InnoUI/";
import { InputField } from "@components/";
import theme from "@theme/";
import { useSelector } from "react-redux";
import { apiPost } from "api/callApi";
import DistributedListDropdown from "components/DistributedListDropdown";
import UserComponent from "./UserComponent";
import { showAlert } from "utils/helper";
import { Checkbox } from "@mui/material";
import Shimmer from "./Shimmer";

const NotificationContent = ({ data, setData }) => {
  const dropdownRef = useRef(null);
  const [occupants, setOccupants] = useState([]);
  const [roles, setRoles] = useState([]);
  const [usersByRole, setUsersByRole] = useState({});
  const [item, setItem] = useState({ listName: "" });
  const [roleSelections, setRoleSelections] = useState({});
  const [loading, setLoading] = useState(null);

  const occupantOption = useSelector(
    (state) => state?.occupantOption?.data || []
  );
  const currentProject = useSelector(
    (state) => state?.currentProject?.data || null
  );

  const occupantData = occupantOption.map((option) => ({
    id: option?.id,
    name: option?.occupantName,
  }));

  const handleChange = (property, value, data) => {
    if (property === "selectList") {
      if (!data || data.length === 0) {
        setData((prev) => ({
          ...prev,
          occupants: [],
          selectList: value,
        }));
      } else {
        // Find the selected item by its ID
        const selectedData = data.find((item) => item.id === value);

        // Check if the selected item has usersData
        if (selectedData && selectedData.usersData) {
          // Parse the usersData of the selected item
          const parsedUsersData = JSON.parse(selectedData.usersData);

          // Update the state with only the selected user's data
          setData((prev) => ({
            ...prev,
            occupants: parsedUsersData,
            selectList: value,
          }));
        }
      }
    }

    // Update the dropdown or property value
    setItem((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  const getRoles = async (requestType) => {
    try {
      setLoading(requestType);
      const response = await apiPost(
        `/v1/options/getroles?active=${true}`,
        {
          projectId: currentProject?.id || null,
          projectType: 0,
        },
        true
      );
      setRoles(response.data);
      setLoading(null);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setLoading(null);
    }
  };

  const getUsers = async (requestType) => {
    try {
      setLoading(requestType);
      const responses = await Promise.all(
        roles.flatMap((role) =>
          occupants.map((occupant) =>
            apiPost(
              "/v1/bulkjournal/getuserdetail",
              {
                roleId: role.roleId,
                occupantId: occupant.id,
              },
              true
            )
          )
        )
      );

      const usersMap = {};
      responses.forEach((response, index) => {
        const roleId = roles[Math.floor(index / occupants.length)].roleId;
        const occupantId = occupants[index % occupants.length].id;

        if (!usersMap[roleId]) usersMap[roleId] = {};
        usersMap[roleId][occupantId] = response.data;
      });

      setUsersByRole(usersMap);
      setLoading(null);
    } catch (error) {
      setLoading(null);
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    setOccupants(occupantData);
    getRoles("userList");
  }, []);

  useEffect(() => {
    if (roles.length > 0 && occupants.length > 0) {
      getUsers("userList");
    }
  }, [roles, occupants]);

  // Filter out roles that have no users
  const filteredRoles = roles.filter((role) => {
    // Check if any second-level key (like 3, 5, 7) has users for this role
    return Object.values(usersByRole[role.roleId] || {}).some(
      (users) => users.length > 0
    );
  });

  const toggleRoleSelection = (roleId) => {
    setData((prevData) => {
      const updatedOccupants = { ...prevData.occupants };

      // Determine if the role is already fully selected across all occupants
      const isRoleSelected = occupants.every(
        (occupant) =>
          updatedOccupants[occupant.id]?.[roleId]?.length ===
          usersByRole[roleId][occupant.id]?.length
      );

      if (isRoleSelected) {
        // If the role is fully selected, deselect all users for this role across all occupants
        Object.keys(updatedOccupants).forEach((occupantId) => {
          if (updatedOccupants[occupantId][roleId]) {
            delete updatedOccupants[occupantId][roleId];
          }

          // If no roles are left for this occupant, remove the occupant entirely
          if (Object.keys(updatedOccupants[occupantId]).length === 0) {
            delete updatedOccupants[occupantId];
          }
        });
      } else {
        // If the role is not fully selected, select it for all users across all occupants
        occupants.forEach((occupant) => {
          if (!updatedOccupants[occupant.id]) {
            updatedOccupants[occupant.id] = {};
          }

          // Ensure all users for this role are selected
          updatedOccupants[occupant.id][roleId] =
            usersByRole[roleId][occupant.id]?.map((user) => user.id) || [];
        });
      }

      return { ...prevData, occupants: updatedOccupants };
    });

    // Toggle the role's selection state
    setRoleSelections((prev) => ({
      ...prev,
      [roleId]: !prev[roleId],
    }));
  };

  const isRoleChecked = (roleId) => {
    return occupants.every((occupant) =>
      usersByRole[roleId]?.[occupant.id]?.every((user) =>
        data.occupants[occupant.id]?.[roleId]?.includes(user.id)
      )
    );
  };

  const toggleUserSelection = (occupantId, roleId, userId) => {
    setData((prevData) => {
      const updatedOccupants = { ...prevData.occupants };
      if (!updatedOccupants[occupantId]) updatedOccupants[occupantId] = {};
      if (!updatedOccupants[occupantId][roleId])
        updatedOccupants[occupantId][roleId] = [];

      const userIndex = updatedOccupants[occupantId][roleId].indexOf(userId);
      if (userIndex === -1) {
        updatedOccupants[occupantId][roleId].push(userId);
      } else {
        updatedOccupants[occupantId][roleId].splice(userIndex, 1);
        if (updatedOccupants[occupantId][roleId].length === 0)
          delete updatedOccupants[occupantId][roleId];
        if (Object.keys(updatedOccupants[occupantId]).length === 0)
          delete updatedOccupants[occupantId];
      }

      return { ...prevData, occupants: updatedOccupants };
    });
  };

  const sendSelectedUsersData = async (requestType) => {
    if (
      !data.occupants ||
      typeof data.occupants !== "object" ||
      Object.keys(data.occupants).length === 0 ||
      Object.values(data.occupants).every(
        (val) =>
          typeof val !== "object" ||
          Object.keys(val).length === 0 ||
          Object.values(val).every(
            (subVal) => Array.isArray(subVal) && subVal.length === 0
          )
      )
    ) {
      showAlert({ title: "Error", text: "Please select users", icon: "error" });
      return;
    }
    try {
      setLoading(requestType);
      const response = await apiPost("/v1/bulkjournal/addlist", {
        projectId: currentProject?.id || null,
        usersData: JSON.stringify(data.occupants),
        listName: item.listName,
      });
      showAlert({ title: "Success", text: response.message, icon: "success" });
      setItem("");
      setLoading(null);
      // Refresh the dropdown data after successful API call
      dropdownRef.current?.refreshData();
    } catch (error) {
      setLoading(null);
      console.error("Error assigning users:", error);
      showAlert({
        title: "Error",
        text: error.response.data.message,
        icon: "error",
      });
    }
  };

  return (
    <>
      <SC.LabelContainer style={{ width: "70%" }}>
        <SC.Label>Distribution List</SC.Label>
        <SC.Children>
          <SC.Wrapper>
            <DistributedListDropdown
              name="selectList"
              onChange={handleChange}
              defaultValue={data?.selectList}
              ref={dropdownRef}
            />
          </SC.Wrapper>
        </SC.Children>
      </SC.LabelContainer>

      <SC.TableContainer>
        <SC.TableWrapper>
          <SC.ScrollableContainer>
            <SC.Table>
              <SC.TableHeader>
                <tr>
                  <SC.HeaderCell>Occupant</SC.HeaderCell>
                  {filteredRoles.map((role) => (
                    <SC.HeaderCell key={role.roleId}>
                      {loading === "userList" ? (
                        <Shimmer />
                      ) : (
                        <div style={{ display: "flex" }}>
                          <Checkbox
                            checked={isRoleChecked(role.roleId)}
                            onChange={() => toggleRoleSelection(role.roleId)}
                            sx={{
                              "&.Mui-checked": {
                                color: theme.colors.themeColor,
                                border: "1px solid white",
                              },
                              padding: "0px",
                            }}
                            size="small"
                          />
                          <SC.Label> {role.role}</SC.Label>
                        </div>
                      )}
                    </SC.HeaderCell>
                  ))}
                </tr>
              </SC.TableHeader>
              <SC.TableBody>
                {occupants.map((occupant) => (
                  <tr key={occupant.id}>
                    {loading === "userList" ? (
                      <Shimmer />
                    ) : (
                      <SC.TableCell>{occupant.name}</SC.TableCell>
                    )}
                    {filteredRoles.map(({ roleId }) => (
                      <SC.TableCell key={roleId}>
                        {loading === "userList" ? (
                          <Shimmer />
                        ) : (
                          (usersByRole[roleId]?.[occupant.id] || []).map(
                            (user) => (
                              <UserComponent
                                key={user.id}
                                user={user}
                                roleId={roleId}
                                occupantId={occupant.id}
                                isUserSelected={(occupantId, roleId, userId) =>
                                  data.occupants[occupantId]?.[
                                    roleId
                                  ]?.includes(userId) ?? false
                                }
                                toggleUserSelection={toggleUserSelection}
                              />
                            )
                          )
                        )}
                      </SC.TableCell>
                    ))}
                  </tr>
                ))}
              </SC.TableBody>
            </SC.Table>
          </SC.ScrollableContainer>
        </SC.TableWrapper>
      </SC.TableContainer>

      <SC.LabelContainer style={{ width: "70%" }}>
        <SC.Label>List Name</SC.Label>
        <SC.Children>
          <SC.Wrapper
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <form
              autoComplete="off"
              style={{ width: "100%", height: "35px" }}
              onSubmit={(event) => {
                event.preventDefault();
              }}
            >
              <InputField
                name="listName"
                value={item?.listName}
                onChange={handleChange}
                required
                maxLength={255}
                style={{ width: "100%", height: "34px" }}
              />
            </form>
            <Button
              caption={"Save"}
              color="white"
              fontSize="12px"
              onClick={() => sendSelectedUsersData("listname")}
              style={{
                background: theme.gradient.themeGradient,
                color: theme.colors.white,
                minWidth: "80px",
                height: "30px",
                borderRadius: "3px",
              }}
              disabled={loading == "listname"}
            />
          </SC.Wrapper>
        </SC.Children>
      </SC.LabelContainer>
    </>
  );
};

export default NotificationContent;
